import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Building2, MapPin, Calculator, Heart } from 'lucide-react';
import { useScrollDirection } from '../hooks/useScrollDirection';

interface MobileNavigationProps {
  hideNav?: boolean;
}

export default function MobileNavigation({ hideNav }: MobileNavigationProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const isVisible = useScrollDirection();

  const navigationItems = [
    { path: '/sehirler', icon: Building2, label: 'Şehirler' },
    { path: '/', icon: MapPin, label: 'Yakındaki' },
    { path: '/favoriler', icon: Heart, label: 'Favoriler' },
    { path: '/taksi-ucreti-hesaplama', icon: Calculator, label: 'Hesapla' }
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <nav className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-50 md:hidden transition-transform duration-300 ${
      isVisible && !hideNav ? 'translate-y-0' : 'translate-y-[150%]'
    }`}>
      <div className="relative">
        {/* Glass Container */}
        <div className="relative px-2 py-2 bg-[#1a1d24]/95 backdrop-blur-md rounded-2xl border border-white/10 shadow-2xl">
          {/* Navigation Items */}
          <div className="flex items-center gap-2">
            {navigationItems.map(({ path, icon: Icon, label }) => {
              const active = isActive(path);
              
              return (
                <button
                  key={path}
                  onClick={() => navigate(path)}
                  className="relative group"
                >
                  <div className="flex flex-col items-center">
                    {/* Button Container with Border Animation */}
                    <div className={`
                      relative p-4 rounded-xl transition-all duration-300
                      ${active 
                        ? 'bg-gradient-to-br from-yellow-400 to-yellow-500 border-2 border-yellow-400 shadow-lg shadow-yellow-400/20'
                        : 'border-2 border-transparent hover:border-yellow-400/50'
                      }
                      before:absolute before:inset-0 before:rounded-xl before:border-2 before:border-yellow-400/0
                      before:transition-all before:duration-300
                      group-hover:before:scale-110 group-hover:before:border-yellow-400/20 before:opacity-0
                      group-hover:before:opacity-100
                    `}>
                      {/* Icon with Animation */}
                      <Icon className={`
                        w-6 h-6 transition-all duration-300
                        ${active 
                          ? 'text-black animate-bounce-subtle'
                          : 'text-white/60 group-hover:text-yellow-400'
                        }
                        ${path === '/favoriler' && active ? 'fill-current' : ''}
                      `} />

                      {/* Glow Effect */}
                      {active && (
                        <div className="absolute inset-0 rounded-xl bg-yellow-400/20 blur-lg -z-10" />
                      )}
                    </div>

                    {/* Label */}
                    <span className={`
                      absolute -bottom-6 left-1/2 -translate-x-1/2 text-xs font-medium
                      transition-all duration-300 whitespace-nowrap opacity-0 group-hover:opacity-100
                      ${active ? 'text-yellow-400' : 'text-white/60'}
                    `}>
                      {label}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Background Blur Effect */}
        <div className="absolute inset-0 -z-10 bg-[#1a1d24]/80 rounded-2xl blur-xl" />
      </div>

      {/* iOS Safe Area */}
      <div className="h-safe-bottom" />
    </nav>
  );
}